// src/components/ZiiChatController.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import ChatWidget from "./ChatWidget";
import useAiChat from "../../hooks/useAiChat";

/**
 * Figures out scope + groupId from the current URL
 */
function useChatContextFromLocation() {
  const location = useLocation();
  const { pathname } = location;

  return useMemo(() => {
    // Landing-like pages
    if (pathname === "/" || pathname.startsWith("/auth")) {
      return { scope: "landing", groupId: null };
    }

    // Dashboard
    if (pathname.startsWith("/dashboard")) {
      return { scope: "dashboard", groupId: null };
    }

    // Group admin: /groups/:groupId/admin
    const adminMatch = pathname.match(/^\/groups\/([^/]+)\/admin/);
    if (adminMatch) {
      return { scope: "group_admin", groupId: adminMatch[1] };
    }

    // Group member space: /groups/:groupId/space
    const spaceMatch = pathname.match(/^\/groups\/([^/]+)\/space/);
    if (spaceMatch) {
      return { scope: "group_member", groupId: spaceMatch[1] };
    }

    // Generic group routes: /groups/:groupId/...
    const groupMatch = pathname.match(/^\/groups\/([^/]+)/);
    if (groupMatch) {
      return { scope: "group_member", groupId: groupMatch[1] };
    }

    // Fallback: treat as dashboard
    return { scope: "dashboard", groupId: null };
  }, [pathname]);
}

const ZiiChatController = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { scope, groupId } = useChatContextFromLocation();
  const { messages, sending, sendMessage } = useAiChat({ scope, groupId });

  // Expose global toggle for the button in index.html
  useEffect(() => {
    const toggle = () => setIsOpen((prev) => !prev);
    window.toggleZiiChat = toggle;

    return () => {
      if (window.toggleZiiChat === toggle) {
        delete window.toggleZiiChat;
      }
    };
  }, []);

  const handleClose = () => setIsOpen(false);

  const handleSend = async (text) => {
    await sendMessage(text);
  };

  return (
    <ChatWidget
      isOpen={isOpen}
      onClose={handleClose}
      messages={messages}
      onSend={handleSend}
      isSending={sending}
    />
  );
};

export default ZiiChatController;
